import { API_BASE_URL, apiGet, apiPostFormRaw, getTenantId } from './client';
import type {
  LegacyRateImportLogRow,
  LegacyRatesImportResult,
  LegacyRatesIssue,
} from '../types/LegacyRatesImport';

/** A rejected load: the server refuses the WHOLE text with 400 { error, issues:[{line,reason}] }. */
export class LegacyRatesImportError extends Error {
  readonly issues: LegacyRatesIssue[];
  constructor(message: string, issues: LegacyRatesIssue[]) {
    super(message);
    this.name = 'LegacyRatesImportError';
    this.issues = issues;
  }
}

const BASE = '/api/legacy-rates-import';

export async function importLegacyRates(input: {
  file?: File | null;
  text?: string;
}): Promise<LegacyRatesImportResult> {
  const form = new FormData();
  if (input.file) form.append('file', input.file);
  else form.append('text', input.text ?? '');
  const res = await apiPostFormRaw(BASE, form);
  if (!res.ok) {
    let message = `${res.status}`;
    let issues: LegacyRatesIssue[] = [];
    try {
      const data = await res.json();
      if (data && typeof data.error === 'string') message = data.error;
      if (data && Array.isArray(data.issues)) issues = data.issues as LegacyRatesIssue[];
    } catch {
      // not JSON
    }
    throw new LegacyRatesImportError(message, issues);
  }
  return (await res.json()) as LegacyRatesImportResult;
}

export function getLegacyRatesImportLog(): Promise<LegacyRateImportLogRow[]> {
  return apiGet<LegacyRateImportLogRow[]>(`${BASE}/log`);
}

// Fetched as a blob (the link needs credentials + the tenant header) and saved.
async function download(path: string, fileName: string): Promise<void> {
  const headers: Record<string, string> = {};
  const tenantId = getTenantId();
  if (tenantId !== null) headers['X-Tenant-Id'] = String(tenantId);
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include', headers });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const downloadLegacyExportQuery = () => download(`${BASE}/export-query`, 'export_rates_for_lop.sql');
export const downloadLegacyRatesSample = () => download(`${BASE}/sample`, 'legacy-rates-sample.txt');
